import { t } from "@redotech/redo-i18n";
import { SettingNavigationProvider } from "@redotech/redo-merchant-app-common/global-search/setting-search/setting-navigator-context";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { settingsPagePermission } from "@redotech/redo-merchant-app-common/routing/settings-page-permission";
import { SettingsSectionEnum } from "@redotech/redo-web/arbiter-components/navigation/redo-settings-nav";
import { matchPath } from "react-router-dom";
import {
  aiSettingsRoutes,
  inAiKnowledgeView,
} from "../../../setting/ai-agent/route";
import { conversionRoutes } from "../../../setting/conversion/route";
import { inFaqBuilder, inFaqHomeView } from "./faq-routes";
import { defaultChildRoute } from "./legacy-redirects";
import { chargebackSettingsRoutes } from "./settings-routes/chargeback";
import { customerAccountSettingsRoutes } from "./settings-routes/customer-account";
import { fulfillmentSettingsRoutes as originalFulfillmentSettingsRoutes } from "./settings-routes/fulfillment";
import { fulfillmentSettingsRoutes as newFulfillmentSettingsRoutes } from "./settings-routes/fulfillment.new";
import { generalSettingsRoutes } from "./settings-routes/general";
import { internationalSettingsRoutes } from "./settings-routes/international";
import { inventorySettingsRoutes } from "./settings-routes/inventory";
import { inventorySettingsRoutes as newInventorySettingsRoutes } from "./settings-routes/inventory.new";
import { marketingSettingsRoutes } from "./settings-routes/marketing";
import { orderTrackingSettingsRoutes } from "./settings-routes/order-tracking";
import { paymentsSettingsRoutes } from "./settings-routes/payments";
import { productSettingsRoutes } from "./settings-routes/product";
import { returnSettingsRoutes } from "./settings-routes/return";
import { shippingSettingsRoutes as originalShippingSettingsRoutes } from "./settings-routes/shipping";
import { shippingSettingsRoutes as newShippingSettingsRoutes } from "./settings-routes/shipping.new";
import { suppliesSettingsRoutes } from "./settings-routes/supplies";
import { supportSettingsRoutes } from "./settings-routes/support";
import { warrantySettingsRoutes } from "./settings-routes/warranty";
import { shippingSettingsFlagRoutes } from "./shipping-settings-flag-routes";

const fullscreen = (url: string) => {
  return url.includes("ad-blocks/") && !url.endsWith("ad-blocks/");
};

const inNewFlowEditor = (url: string) => {
  return (
    url.includes("/flows/returns") ||
    url.includes("/flows/claims") ||
    url.includes("/flows/warranties") ||
    url.includes("/flows/withdrawals") ||
    inCompensationFlowEditor(url) ||
    inRestockingFlowEditor(url)
  );
};

const inCompensationFlowEditor = (url: string) => {
  return url.includes("compensation-flow-editor");
};

const inRestockingFlowEditor = (url: string) => {
  return url.includes("restocking-flow-editor");
};

const inLocationEditor = (url: string) => {
  return url.includes("location-flow-editor");
};

const inLabelsEditor = (url: string) => {
  return url.includes("labels-flow-editor");
};

const inForge = (url: string) => {
  return (
    (url.includes("reviews-displays-widgets/") &&
      !url.endsWith("reviews-displays-widgets/")) ||
    (url.includes("upsell-page/") && !url.endsWith("upsell-page/")) ||
    (url.includes("oms/automations") && !url.endsWith("oms/automations")) ||
    inFaqBuilder(url) ||
    url.includes("oms/warehouse-builder") ||
    url.includes("conversion/general/checkout-widgets-builder") ||
    inWorkOrderFlowBuilder(url) ||
    inSuppliesShippingBuilder(url)
  );
};

const inPhoneSetup = (url: string) => {
  return url.includes("phone-setup");
};

const inBilling = (url: string) => {
  return url.includes("/settings/billing");
};

const inAutomationsList = (url: string) => {
  return url.endsWith("oms/automations");
};

const inWorkOrderFlowsList = (url: string) => {
  return url.endsWith("oms/work-order-flows");
};

const inWorkOrderFlowBuilder = (url: string) => {
  return url.includes("oms/work-order-flows/");
};

const inSuppliesShippingBuilder = (url: string) => {
  return url.includes("supplies/shipping-rules");
};

const inProcessingSettings = (url: string) => {
  return (
    url.endsWith("settings/automations/processing") ||
    url.endsWith("settings/automations/processing/")
  );
};

const inReturnProcessingFlowBuilder = (url: string) => {
  return (
    url.includes("settings/automations/") &&
    !inProcessingSettings(url) &&
    !inCompensationFlowEditor(url) &&
    !inRestockingFlowEditor(url)
  );
};

const inPortalBuilder = (url: string) => {
  return url.includes("settings/portal-builder");
};

const inCoverageBuilder = (url: string) => {
  return url.includes("coverage-builder");
};

/** One test's editor; the list itself keeps the settings chrome. */
const inAbTestEditor = (url: string) => {
  return (
    !!matchPath("/stores/:storeId/settings/ab-tests/:testId/*", url) ||
    !!matchPath("/settings/ab-tests/:testId/*", url)
  );
};

const inCoverageSettings = (url: string) => {
  return url.includes("settings/coverage") && !inCoverageBuilder(url);
};

const inReturnsSettings = (url: string) => {
  return /\/settings\/returns\/?$/.test(url);
};

const inPostProcessingSettings = (url: string) => {
  return /\/settings\/post-processing\/?$/.test(url);
};

const inWarrantyRegistrationSettings = (url: string) => {
  return /\/settings\/registrations\/?$/.test(url);
};

const inOrderTrackingSettings = (url: string) => {
  return /\/settings\/order-tracking\/?$/.test(url);
};

const inOrderEditingSettings = (url: string) => {
  return /\/settings\/order-management\/order-editing\/?$/.test(url);
};

const inAssessmentsSettings = (url: string) => {
  return /\/settings\/assessments\/?$/.test(url);
};

const inFreightSettings = (url: string) => {
  return /\/settings\/freight\/?$/.test(url);
};

const inWithdrawalsSettings = (url: string) => {
  return /\/settings\/withdrawals\/?$/.test(url);
};

const inCarriersSettings = (url: string) => {
  return /\/settings\/carriers\/?$/.test(url);
};

const inBrandKitSettings = (url: string) => {
  return /\/settings\/brand-kit\/?$/.test(url);
};

const inIntegrationsHub = (url: string) => {
  return /\/settings\/integrations\/?$/.test(url);
};

/** Shared vendor detail page. Dedicated routes such as Cordial keep their padding. */
const inIntegrationVendorSettings = (url: string) => {
  return /\/settings\/integrations\/vendor\/[^/]+\/?$/.test(url);
};

const inCommunicationSettings = (url: string) => {
  return /\/settings\/communication\/?$/.test(url);
};

const inSupportTopicsSettings = (url: string) => {
  return /\/settings\/topics\/?$/.test(url);
};

const inProductsSettings = (url: string) => {
  return /\/settings\/products\/?$/.test(url);
};

const inCustomerAccountsStorefrontDisplay = (url: string) => {
  return /\/settings\/customer-accounts-storefront-display\/?$/.test(url);
};

const inCustomerAccountsAdBlocks = (url: string) => {
  return /\/settings\/customer-accounts-ad-blocks\/?$/.test(url);
};

const inNotificationsSettings = (url: string) => {
  return /\/settings\/notifications\/?$/.test(url);
};

const inAiGeneralSettings = (url: string) => {
  return /\/settings\/ai\/general\/?$/.test(url);
};

const inAiSkillsSettings = (url: string) => {
  return /\/settings\/ai\/skills\/?$/.test(url);
};

const inRolesSettings = (url: string) => {
  return (
    !!matchPath("/stores/:storeId/settings/roles", url) ||
    !!matchPath("/settings/roles", url)
  );
};

const inUsersSettings = (url: string) => {
  return (
    /\/settings\/users(\/[^/]+)?\/?$/.test(url) ||
    /\/settings\/personal\/profile\/?$/.test(url)
  );
};

const inDeveloperSettings = (url: string) => {
  return /\/settings\/developer\/?$/.test(url);
};

const inWebhookDetailSettings = (url: string) => {
  return /\/settings\/developer\/webhooks\/[^/]+\/?$/.test(url);
};

const inUserGroupsSettings = (url: string) => {
  return /\/settings\/user-groups\/?$/.test(url);
};

const inSupportGeneralSettings = (url: string) => {
  return /\/settings\/general\/?$/.test(url);
};

const inSupportTagsSettings = (url: string) => {
  return /\/settings\/tags\/?$/.test(url);
};

const inCustomerWidgetSettings = (url: string) => {
  return url.includes("settings/customer-widget");
};

/** Marketing table settings use the Returns settings shell. */
const inMarketingSettingsShell = (url: string) => {
  return /\/settings\/(audience|email-domain|marketing-agent|offer-page-configuration|recover-settings|sms-marketing|subscribe-keywords)\/?$/.test(
    url,
  );
};

const inConversionSettings = (url: string) => {
  return (
    url.includes("settings/conversion") &&
    !url.includes("checkout-widgets-builder")
  );
};

const inChargebacksSettings = (url: string) => {
  return url.includes("settings/chargebacks");
};

const inHsaSettings = (url: string) => {
  return url.includes("settings/payments/hsa");
};

/** Channels index only. Connect, setup, and dev-tool routes keep their padding. */
const inSupportChannelsSettings = (url: string) => {
  return /\/settings\/channels\/?$/.test(url);
};

const inInternationalReturnsSettings = (url: string) => {
  return /\/settings\/international\/returns\/?$/.test(url);
};

const inSupportMacrosSettings = (url: string) => {
  return /\/settings\/macros\/?$/.test(url);
};

/** Rules list only. Editors and the new-rule flow keep their own layout. */
const inSupportRulesList = (url: string) => {
  return /\/settings\/rules\/?$/.test(url);
};

const inShippingSettings = (url: string) => {
  return (
    url.includes("settings/shipping") &&
    !inLocationEditor(url) &&
    !inLabelsEditor(url)
  );
};

export const isPackingSlipBuilderRoute = (url: string) => {
  return (
    !!matchPath(
      "/stores/:storeId/settings/oms/printing/packing-slips/:packingSlipId",
      url,
    ) || !!matchPath("/settings/oms/printing/packing-slips/:packingSlipId", url)
  );
};

const inShippingLabelBuilder = (url: string) => {
  return url.includes("settings/oms/printing/shipping-labels");
};

export function isFullPageSettingsRoute(url: string) {
  return (
    fullscreen(url) ||
    inForge(url) ||
    inPhoneSetup(url) ||
    inNewFlowEditor(url) ||
    inLocationEditor(url) ||
    inLabelsEditor(url) ||
    inReturnProcessingFlowBuilder(url) ||
    inPortalBuilder(url) ||
    inCoverageBuilder(url) ||
    inAbTestEditor(url) ||
    isPackingSlipBuilderRoute(url) ||
    inShippingLabelBuilder(url)
  );
}

const isWarehouseLayout = (url: string) => {
  return url.endsWith("oms/warehouse-layouts");
};

const isFullwidthTable = (url: string) => {
  return (
    url.includes("settings/attributes") ||
    url.includes("settings/exchanges/guidance") ||
    url.includes("settings/international/catalog")
  );
};

const inRuleEditor = (url: string) => {
  return (
    url.includes("settings/rules/edit-v3/") ||
    url.includes("settings/rules/edit-default/")
  );
};

export function hideSettingsPagePadding(
  url: string,
  _search?: { search: string },
) {
  if (inAiGeneralSettings(url)) {
    return true;
  }
  return (
    inAiKnowledgeView(url) ||
    inAiSkillsSettings(url) ||
    inAutomationsList(url) ||
    inWorkOrderFlowsList(url) ||
    fullscreen(url) ||
    inForge(url) ||
    inReturnProcessingFlowBuilder(url) ||
    inPhoneSetup(url) ||
    inBilling(url) ||
    isWarehouseLayout(url) ||
    inFaqHomeView(url) ||
    isFullwidthTable(url) ||
    (url.includes("customer-portal") && !inNewFlowEditor(url)) ||
    inPortalBuilder(url) ||
    inCoverageBuilder(url) ||
    inAbTestEditor(url) ||
    inCoverageSettings(url) ||
    inReturnsSettings(url) ||
    inPostProcessingSettings(url) ||
    inWarrantyRegistrationSettings(url) ||
    inOrderTrackingSettings(url) ||
    inOrderEditingSettings(url) ||
    inAssessmentsSettings(url) ||
    inCarriersSettings(url) ||
    inFreightSettings(url) ||
    inWithdrawalsSettings(url) ||
    inBrandKitSettings(url) ||
    inIntegrationsHub(url) ||
    inIntegrationVendorSettings(url) ||
    inCommunicationSettings(url) ||
    inProductsSettings(url) ||
    inCustomerAccountsStorefrontDisplay(url) ||
    inCustomerAccountsAdBlocks(url) ||
    inNotificationsSettings(url) ||
    inRolesSettings(url) ||
    inUsersSettings(url) ||
    inDeveloperSettings(url) ||
    inWebhookDetailSettings(url) ||
    inUserGroupsSettings(url) ||
    inSupportGeneralSettings(url) ||
    inSupportTagsSettings(url) ||
    inSupportTopicsSettings(url) ||
    inCustomerWidgetSettings(url) ||
    inMarketingSettingsShell(url) ||
    inConversionSettings(url) ||
    inChargebacksSettings(url) ||
    inHsaSettings(url) ||
    inSupportChannelsSettings(url) ||
    inInternationalReturnsSettings(url) ||
    inSupportMacrosSettings(url) ||
    inSupportRulesList(url) ||
    inShippingSettings(url) ||
    isPackingSlipBuilderRoute(url) ||
    inShippingLabelBuilder(url) ||
    inRuleEditor(url)
  );
}

const settingsRoutesBySection: Record<
  SettingsSectionEnum,
  PermissionedRouteObject[] | PermissionedRouteObject
> = {
  [SettingsSectionEnum.WAREHOUSE]: [],
  [SettingsSectionEnum.DOCUMENTS_AND_PRINTING]: [],
  [SettingsSectionEnum.GENERAL]: generalSettingsRoutes,
  [SettingsSectionEnum.AI]: aiSettingsRoutes,
  [SettingsSectionEnum.CHARGEBACKS]: chargebackSettingsRoutes,
  [SettingsSectionEnum.CONVERSION]: conversionRoutes,
  [SettingsSectionEnum.CUSTOMER_ACCOUNTS]: customerAccountSettingsRoutes,
  [SettingsSectionEnum.INTERNATIONAL]: internationalSettingsRoutes,
  [SettingsSectionEnum.INVENTORY_MANAGEMENT]: shippingSettingsFlagRoutes(
    newInventorySettingsRoutes,
    inventorySettingsRoutes,
  ),
  [SettingsSectionEnum.MARKETING]: marketingSettingsRoutes,
  [SettingsSectionEnum.ORDER_FULFILLMENT]: shippingSettingsFlagRoutes(
    newFulfillmentSettingsRoutes,
    originalFulfillmentSettingsRoutes,
  ),
  [SettingsSectionEnum.ORDER_TRACKING]: orderTrackingSettingsRoutes,
  [SettingsSectionEnum.PAYMENTS]: paymentsSettingsRoutes,
  [SettingsSectionEnum.PRODUCTS]: productSettingsRoutes,
  [SettingsSectionEnum.RECOVER]: [],
  [SettingsSectionEnum.RETURNS_AND_CLAIMS]: returnSettingsRoutes,
  [SettingsSectionEnum.SHIPPING]: shippingSettingsFlagRoutes(
    newShippingSettingsRoutes,
    originalShippingSettingsRoutes,
  ),
  [SettingsSectionEnum.SUPPLIES]: suppliesSettingsRoutes,
  [SettingsSectionEnum.SUPPORT]: supportSettingsRoutes,
  [SettingsSectionEnum.WARRANTIES]: warrantySettingsRoutes,
  [SettingsSectionEnum.PERSONAL]: [
    {
      handle: {
        get breadcrumb() {
          return t("Personal", {
            context:
              "merchant settings breadcrumb for personal account settings",
          });
        },
        permission: settingsPagePermission("personal/profile"),
      },
      path: "personal",
      children: [
        defaultChildRoute("profile"),
        {
          handle: {
            get breadcrumb() {
              return t("Profile settings");
            },
          },
          path: "profile",
          lazy: async () => {
            const { ProfilePage } =
              await import("@redotech/redo-merchant-general-app/setting/user/profile-page");
            return { Component: ProfilePage };
          },
        },
      ],
    },
  ],
};

const routeChildren = Object.values(settingsRoutesBySection).reduce<
  PermissionedRouteObject[]
>((acc, section) => {
  if (Array.isArray(section)) {
    return [...acc, ...section];
  } else {
    return [...acc, section];
  }
}, [] as PermissionedRouteObject[]);

export const settingRoutes: PermissionedRouteObject[] = [
  {
    handle: {
      get breadcrumb() {
        return t("Settings", {
          context: "root breadcrumb for merchant settings pages",
        });
      },
      breadcrumbNavigate: false,
    },
    lazy: async () => {
      const { Page } = await import("../../page");
      return {
        element: (
          <SettingNavigationProvider>
            <Page
              hideHeader={isFullPageSettingsRoute}
              hideNavbar={isFullPageSettingsRoute}
              hidePadding={hideSettingsPagePadding}
              hideSearchBarAndBanner={isFullPageSettingsRoute}
            />
          </SettingNavigationProvider>
        ),
      };
    },
    children: routeChildren,
  },
];
